import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AdminDetailGrid } from '@/components/patterns/admin-page'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { EmptyState } from '@/components/patterns/empty-state'
import { BillingBooleanSettingField } from '@/components/customers/BillingBooleanSettingField'
import {
  HouseAccountSettingsSection,
  getPaymentTermsDisabledTooltip,
  isHouseAccountEnabled,
  setHouseAccountEnabled,
} from '@/components/customers/HouseAccountSettingsSection'
import {
  CustomerBillingSetupCard,
  CustomerCardsOnFileCard,
  CustomerSummaryStrip,
  CustomerUnpaidOrdersCard,
} from '@/components/customers/CustomerBillingOverview'
import { CustomerPaymentDialog } from '@/components/customers/CustomerPaymentDialog'
import { CustomerStatementDialog } from '@/components/customers/CustomerStatementDialog'
import { CustomerDetailHeader } from '@/components/customers/CustomerDetailHeader'
import { CustomerActivityWorkspace } from '@/components/customers/CustomerActivityWorkspace'
import { CustomerContactAside } from '@/components/customers/CustomerContactAside'
import { AddressVerificationDialog } from '@/components/forms/Address/AddressVerificationDialog'
import {
  applySuggestedDeliveryAddress,
  buildDeliveryAddressSearchString,
  createManuallyVerifiedAddress,
  isSuggestedAddressUsable,
} from '@/components/forms/Orders/fulfillment-destination.helpers'
import { OrderDetailPanel } from '@/components/orders/OrderDetailPanel'
import {
  ORDER_ASIDE_FORM_SURFACE_CLASS,
  ORDER_ASIDE_SHEET_CONTENT_CLASS,
} from '@/components/orders/order-aside.styles'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { Typography } from '@/components/ui/typography'
import { Textarea } from '@/components/ui/textarea'
import { usePlacesAutocomplete } from '@/hooks/use-places-autocomplete'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { getAddressRegionOptions } from '@/lib/address-region-options'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { Can, ReadOnly, useAbility } from '@/lib/casl'
import {
  canManageCustomerBilling,
  canViewCustomerReceivables,
} from '@/lib/customer-receivables-access'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import { useAppForm } from '@/lib/form'
import {
  applyCustomerAddressPlace,
  cleanCustomerAddressDraft as cleanAddressDraft,
  clearCustomerAddressVerification as clearAddressVerification,
  customerAddressDraftFrom as addressDraftFrom,
  customerAddressHasValue as addressHasValue,
} from '@/lib/services/customers/customers.address'
import {
  customerQueries,
  useCreateCustomerPosCartMutation,
  useCustomer,
  useCustomerReceivables,
  useUpdateCustomerBillingSettingsMutation,
  useUpdateCustomerMutation,
} from '@/lib/services/customers/customers.query'
import {
  flowerSubscriptionQueries,
  useFlowerSubscriptions,
} from '@/lib/services/flower-subscriptions/flower-subscriptions.query'
import {
  customerContactSchema,
  customerIdSchema,
  customerIdentitySchema,
} from '@/lib/services/customers/customers.schema'
import { cn, getErrorMessage, parseCurrency } from '@/lib/utils'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-form'
import {
  BadgePercent,
  Building2,
  CalendarDays,
  ContactRound,
  HandCoins,
  MapPin,
  Phone,
  Plus,
  ReceiptText,
  Trash2,
  Truck,
  User,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/customers/$customerId')({
  component: CustomerDetailPage,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => customerIdSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'customers')) throw notFound()
    const customerQuery = customerQueries.show({
      organizationId: params.orgId,
      customerId: params.customerId,
    })
    const queries = [context.queryClient.ensureQueryData(customerQuery)]

    if (canViewCustomerReceivables(context.ability)) {
      queries.push(
        context.queryClient.prefetchQuery(
          customerQueries.receivables({
            organizationId: params.orgId,
            customerId: params.customerId,
          })
        )
      )
    }

    if (context.ability.can('list', 'flower-subscriptions')) {
      queries.push(
        context.queryClient.prefetchQuery(
          flowerSubscriptionQueries.index({
            organizationId: params.orgId,
            filters: {
              customerId: params.customerId,
              sort: 'attention',
            },
            page: 1,
            pageSize: 10,
          })
        )
      )
    }

    const [customer] = await Promise.all(queries)

    return { customer, isReadOnly: !context.ability.can('edit', 'customers') }
  },
})

const customerAsideViews = {
  editCustomer: 'editCustomer',
  editPhysicalAddress: 'editPhysicalAddress',
  editBillingAddress: 'editBillingAddress',
  editBillingSettings: 'editBillingSettings',
  editContacts: 'editContacts',
}

function hasRequiredAddressFields(address) {
  return ['line1', 'city', 'state', 'postal_code'].every((field) =>
    String(address?.[field] || '').trim()
  )
}

async function mapSelectedPlaceToCustomerAddress({ form, fieldPrefix, place, geocodePlaceId }) {
  applyCustomerAddressPlace(form, fieldPrefix, place, { isRooftop: false })
  if (place.placeId) {
    const geo = await geocodePlaceId(place.placeId)
    const isRooftop = geo?.locationType === 'ROOFTOP' && geo?.partialMatch !== true
    form.setFieldValue(`${fieldPrefix}.is_rooftop`, Boolean(isRooftop))
  } else {
    form.setFieldValue(`${fieldPrefix}.is_rooftop`, false)
  }
}

function getPaymentTermsOptions() {
  return Object.values(CustomerPaymentTerms).map((term) => ({
    id: term.Value,
    name: term.Name,
  }))
}

function buildCustomerBillingPayload(customer, value) {
  const paymentTermsCode = value.payment_terms_code || CustomerPaymentTerms.DueOnReceipt.Value
  const payLaterAllowed = value.pay_later_allowed !== false
  const allowFulfillmentBeforePayment =
    payLaterAllowed && value.allow_fulfillment_before_payment === true
  const creditLimit = String(value.credit_limit ?? '').trim()
  const customDays = String(value.payment_terms_days ?? '').trim()

  return {
    customerId: customer.id,
    pay_later_allowed: payLaterAllowed,
    allow_fulfillment_before_payment: allowFulfillmentBeforePayment,
    billing_email: value.billing_email?.trim() || null,
    statement_delivery_method: value.statement_delivery_method || 'automatic',
    credit_limit: payLaterAllowed && creditLimit !== '' ? parseCurrency(creditLimit) : null,
    payment_terms_code: allowFulfillmentBeforePayment
      ? paymentTermsCode
      : CustomerPaymentTerms.DueOnReceipt.Value,
    payment_terms_days:
      allowFulfillmentBeforePayment && paymentTermsCode === CustomerPaymentTerms.Custom.Value
        ? Math.max(0, Number.parseInt(customDays, 10) || 0)
        : null,
    tax_exempt: Boolean(value.tax_exempt),
  }
}

function contactDraftFromCustomer(customer) {
  return (customer.contacts ?? []).map((contact) => ({
    id: contact.id,
    name: contact.name || '',
    email: contact.email || '',
    phone: contact.phone || '',
    is_primary: Boolean(contact.is_primary),
  }))
}

function normalizeContactsPayload(contacts) {
  return contacts
    .map((contact) => ({
      id: contact.id,
      name: String(contact.name || '').trim(),
      email: String(contact.email || '').trim() || null,
      phone: String(contact.phone || '').trim() || null,
      is_primary: Boolean(contact.is_primary),
    }))
    .filter((contact) => contact.name)
}

function CustomerAside({ children, open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={ORDER_ASIDE_SHEET_CONTENT_CLASS}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="sr-only">
          <SheetTitle>Customer Actions</SheetTitle>
          <SheetDescription>View and manage customer details and actions</SheetDescription>
        </div>
        {children}
      </SheetContent>
    </Sheet>
  )
}

function CustomerAsideFormFooter({ form, mutation, saveLabel, onCancel, leadingAction = null }) {
  return (
    <div className="shrink-0 px-5 pt-4 pb-5 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">{leadingAction}</div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting, state.errors]}>
            {([canSubmit, isSubmitting, errors]) => (
              <Button
                type="submit"
                disabled={
                  Boolean(errors.length) || isSubmitting || mutation?.isPending || !canSubmit
                }
              >
                {isSubmitting || mutation?.isPending ? 'Saving...' : saveLabel}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </div>
    </div>
  )
}

function CustomerIdentityAsideForm({ customer, closeAside }) {
  const updateCustomerMutation = useUpdateCustomerMutation()
  const form = useAppForm({
    defaultValues: {
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      customer_type: customer.is_business ? 'business' : 'individual',
    },
    validators: {
      onChange: customerIdentitySchema,
      onSubmit: customerIdentitySchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateCustomerMutation.mutateAsync({
            customerId: customer.id,
            name: value.name,
            phone: value.phone,
            email: value.email,
            is_business: value.customer_type === 'business',
          })
          toast.success('Customer updated')
          closeAside?.()
        } catch (error) {
          toast.error('Customer update failed', {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  return (
    <form.AppForm>
      <form.Form className="flex min-h-full flex-col">
        <OrderDetailPanel
          title="Edit Customer"
          subtitle="Update the customer's name, contact information, and customer type."
          icon={User}
          tone="neutral"
          className="shadow-none"
        >
          <div className={ORDER_ASIDE_FORM_SURFACE_CLASS}>
            <form.FormError />
            <div className="grid gap-y-5">
              <form.AppField name="name">
                {(field) => <field.TextInput label="Name" placeholder="Customer name" />}
              </form.AppField>
              <form.AppField name="phone">
                {(field) => <field.PhoneInput label="Phone" />}
              </form.AppField>
              <form.AppField name="email">
                {(field) => <field.EmailInput label="Email" placeholder="customer@example.com" />}
              </form.AppField>
              <form.AppField name="customer_type">
                {(field) => (
                  <field.RadioButtonGroup
                    label="Customer Type"
                    options={[
                      {
                        id: 'individual',
                        name: 'Individual',
                        start: <User className="size-4" />,
                      },
                      {
                        id: 'business',
                        name: 'Business',
                        start: <Building2 className="size-4" />,
                      },
                    ]}
                    classNames={{
                      container: 'w-full',
                      button: 'h-10 min-w-32 gap-2',
                    }}
                  />
                )}
              </form.AppField>
            </div>
          </div>
        </OrderDetailPanel>
        <CustomerAsideFormFooter
          form={form}
          mutation={updateCustomerMutation}
          saveLabel="Save Customer"
          onCancel={closeAside}
        />
      </form.Form>
    </form.AppForm>
  )
}

function CustomerAddressFields({ form, idPrefix, fieldPrefix }) {
  const { geocodePlaceId } = usePlacesAutocomplete()

  return (
    <div className="grid gap-y-5">
      <form.AppField name={`${fieldPrefix}.line1`}>
        {(field) => (
          <field.AddressSearch
            id={`${idPrefix}-${fieldPrefix}-line1`}
            label="Street Address"
            placeholder="Start typing an address..."
            onChange={() => clearAddressVerification(form, fieldPrefix)}
            onAddressSelected={(place) =>
              mapSelectedPlaceToCustomerAddress({
                form,
                fieldPrefix,
                place,
                geocodePlaceId,
              })
            }
          />
        )}
      </form.AppField>
      <form.AppField name={`${fieldPrefix}.line2`}>
        {(field) => (
          <field.TextInput
            id={`${idPrefix}-${fieldPrefix}-line2`}
            label="Apt, Suite, Building"
            placeholder="Apartment, suite, unit, etc."
          />
        )}
      </form.AppField>
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-6 md:col-span-3">
          <form.AppField name={`${fieldPrefix}.city`}>
            {(field) => (
              <field.TextInput
                id={`${idPrefix}-${fieldPrefix}-city`}
                label="City"
                placeholder="City"
              />
            )}
          </form.AppField>
        </div>
        <div className="col-span-3 md:col-span-1">
          <form.AppField name={`${fieldPrefix}.state`}>
            {(field) => (
              <field.SelectInput
                id={`${idPrefix}-${fieldPrefix}-state`}
                label="State"
                placeholder="State"
                options={getAddressRegionOptions(field.state.value)}
              />
            )}
          </form.AppField>
        </div>
        <div className="col-span-3 md:col-span-2">
          <form.AppField name={`${fieldPrefix}.postal_code`}>
            {(field) => (
              <field.TextInput
                id={`${idPrefix}-${fieldPrefix}-postal-code`}
                label="Zip"
                placeholder="Zip code"
              />
            )}
          </form.AppField>
        </div>
      </div>
    </div>
  )
}

function CustomerAddressAsideForm({ customer, addressType, closeAside }) {
  const updateCustomerMutation = useUpdateCustomerMutation()
  const { geocodeAddress } = usePlacesAutocomplete()
  const [verifyPrompt, setVerifyPrompt] = useState(null)
  const isBillingAddress = addressType === 'billing_address'
  const sameAsPhysicalLabel = 'Use physical address as billing address'
  const resolveAddressForSave = async (address) => {
    const hasCoords = address?.lat != null && address?.lng != null
    const hasPlaceId = Boolean(address?.google_place_id)

    if (hasCoords && hasPlaceId) {
      return {
        ...address,
        is_verified: true,
        is_rooftop: Boolean(address.is_rooftop),
      }
    }

    const typedString = buildDeliveryAddressSearchString(address)
    const suggested = typedString ? await geocodeAddress(typedString) : null

    if (!isSuggestedAddressUsable(suggested)) {
      return createManuallyVerifiedAddress(address)
    }

    const decision = await new Promise((resolve) => {
      setVerifyPrompt({
        typedAddress: address,
        suggested,
        resolve,
      })
    })

    if (decision === 'typed') {
      return createManuallyVerifiedAddress(address)
    }

    if (decision === 'suggested') {
      return applySuggestedDeliveryAddress(address, suggested)
    }

    return null
  }
  const form = useAppForm({
    defaultValues: {
      physical_address: addressDraftFrom(customer.physical_address),
      billing_address: addressDraftFrom(customer.billing_address),
      physical_address_is_billing: Boolean(customer.physical_address_is_billing),
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        const address = value[addressType]
        const usesPhysicalForBilling = Boolean(value.physical_address_is_billing)
        const hasAddressDraft = addressHasValue(address)
        let addressForSave = address

        if ((!isBillingAddress || !usesPhysicalForBilling) && hasAddressDraft) {
          if (!hasRequiredAddressFields(address)) {
            toast.error('Address needs a street, city, state, and zip.')
            return
          }

          try {
            addressForSave = await resolveAddressForSave(address)
          } catch (error) {
            toast.error('Address verification failed', {
              description: getErrorMessage(error),
            })
            return
          }

          if (!addressForSave) {
            return
          }
        } else if (!isBillingAddress && !hasAddressDraft && !customer.physical_address) {
          toast.error('Address needs a street, city, state, and zip.')
          return
        }

        const payload = {
          customerId: customer.id,
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
        }

        if (isBillingAddress) {
          payload.physical_address_is_billing = usesPhysicalForBilling
          payload.billing_address =
            usesPhysicalForBilling || !hasAddressDraft ? null : cleanAddressDraft(addressForSave)
        } else {
          const physicalAddress = cleanAddressDraft(addressForSave)

          payload.physical_address = physicalAddress
          if (physicalAddress && !customer.billing_address_id) {
            payload.physical_address_is_billing = true
          }
        }

        try {
          await updateCustomerMutation.mutateAsync(payload)
          toast.success(isBillingAddress ? 'Billing address updated' : 'Physical address updated')
          closeAside?.()
        } catch (error) {
          toast.error(
            isBillingAddress ? 'Billing address update failed' : 'Physical address update failed',
            {
              description: getErrorMessage(error),
            }
          )

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  const physicalAddressIsBilling = useStore(
    form.store,
    (state) => state.values.physical_address_is_billing
  )
  const physicalAddressDraft = useStore(form.store, (state) => state.values.physical_address)
  const billingAddressDraft = useStore(form.store, (state) => state.values.billing_address)
  const title = isBillingAddress ? 'Billing Address' : 'Physical Address'
  const canDeletePhysicalAddress =
    !isBillingAddress &&
    (Boolean(customer.address_id) ||
      Boolean(customer.physical_address) ||
      addressHasValue(physicalAddressDraft))
  const canDeleteBillingAddress =
    isBillingAddress &&
    !physicalAddressIsBilling &&
    (Boolean(customer.billing_address_id) || addressHasValue(billingAddressDraft))

  const clearBillingAddressDraft = () => {
    form.setFieldValue('physical_address_is_billing', false)
    form.setFieldValue('billing_address', addressDraftFrom(null))
  }

  const clearPhysicalAddressDraft = () => {
    form.setFieldValue('physical_address', addressDraftFrom(null))
  }

  const deletePhysicalAddress = async () => {
    if (!customer.physical_address && !customer.address_id) {
      clearPhysicalAddressDraft()
      return
    }

    try {
      await updateCustomerMutation.mutateAsync({
        customerId: customer.id,
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        physical_address: null,
        physical_address_is_billing: false,
      })
      toast.success('Physical address deleted')
      closeAside?.()
    } catch (error) {
      toast.error('Physical address delete failed', {
        description: getErrorMessage(error),
      })
    }
  }

  const deleteBillingAddress = async () => {
    const hasPersistedBillingAddress = Boolean(customer.billing_address_id)

    if (!hasPersistedBillingAddress) {
      clearBillingAddressDraft()
      return
    }

    try {
      await updateCustomerMutation.mutateAsync({
        customerId: customer.id,
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        physical_address_is_billing: false,
        billing_address: null,
      })
      toast.success('Billing address deleted')
      closeAside?.()
    } catch (error) {
      toast.error('Billing address delete failed', {
        description: getErrorMessage(error),
      })
    }
  }
  const deleteBillingAddressAction = canDeleteBillingAddress ? (
    <ConfirmationDialog
      variant="destructive"
      title="Delete billing address"
      description="Are you sure you want to delete this billing address? The customer will not have a billing address until one is added again."
      confirmText="Delete"
      pendingText="Deleting..."
      isPending={updateCustomerMutation.isPending}
      onConfirm={deleteBillingAddress}
      trigger={
        <Button
          type="button"
          variant="outlineDestructive"
          disabled={updateCustomerMutation.isPending}
        >
          <Trash2 className="size-4" />
          Delete Billing Address
        </Button>
      }
    />
  ) : null
  const deletePhysicalAddressAction = canDeletePhysicalAddress ? (
    <ConfirmationDialog
      variant="destructive"
      title="Delete physical address"
      description="Are you sure you want to delete this physical address? The customer will not have a physical address until one is added again."
      confirmText="Delete"
      pendingText="Deleting..."
      isPending={updateCustomerMutation.isPending}
      onConfirm={deletePhysicalAddress}
      trigger={
        <Button
          type="button"
          variant="outlineDestructive"
          disabled={updateCustomerMutation.isPending}
        >
          <Trash2 className="size-4" />
          Delete Physical Address
        </Button>
      }
    />
  ) : null
  const deleteAddressAction = isBillingAddress
    ? deleteBillingAddressAction
    : deletePhysicalAddressAction

  return (
    <form.AppForm>
      <form.Form className="flex min-h-full flex-col">
        <OrderDetailPanel
          title={title}
          subtitle={
            isBillingAddress
              ? 'Update the address used for customer billing and invoices.'
              : 'Update the customer physical address.'
          }
          icon={MapPin}
          tone={isBillingAddress ? 'amber' : 'emerald'}
          className="shadow-none"
        >
          <div className={ORDER_ASIDE_FORM_SURFACE_CLASS}>
            <div className="grid gap-y-5">
              {isBillingAddress ? (
                <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
                  <form.AppField name="physical_address_is_billing">
                    {(field) => (
                      <field.SwitchInput
                        label={sameAsPhysicalLabel}
                        description="Turn this off to enter a different billing address."
                        className="[&_[data-slot=switch]]:border [&_[data-slot=switch]]:border-input"
                      />
                    )}
                  </form.AppField>
                </div>
              ) : null}

              {isBillingAddress && physicalAddressIsBilling ? (
                <Typography
                  variant="supporting"
                  className="block rounded-xl border border-border/60 bg-muted/20 p-3"
                >
                  Billing address will use this customer's physical address.
                </Typography>
              ) : (
                <CustomerAddressFields
                  form={form}
                  idPrefix={customer.id}
                  fieldPrefix={addressType}
                />
              )}
            </div>
          </div>
        </OrderDetailPanel>
        <CustomerAsideFormFooter
          form={form}
          mutation={updateCustomerMutation}
          saveLabel={isBillingAddress ? 'Save Billing Address' : 'Save Physical Address'}
          onCancel={closeAside}
          leadingAction={deleteAddressAction}
        />
      </form.Form>
      <AddressVerificationDialog
        open={Boolean(verifyPrompt)}
        title="Confirm the address"
        description="We found a close match for this customer address. Choose which address to use."
        typedAddress={verifyPrompt?.typedAddress}
        suggested={verifyPrompt?.suggested}
        isSuggestedUsable={isSuggestedAddressUsable(verifyPrompt?.suggested)}
        onChooseTyped={() => {
          verifyPrompt?.resolve?.('typed')
          setVerifyPrompt(null)
        }}
        onChooseSuggested={() => {
          verifyPrompt?.resolve?.('suggested')
          setVerifyPrompt(null)
        }}
        onCancel={() => {
          verifyPrompt?.resolve?.('cancel')
          setVerifyPrompt(null)
        }}
      />
    </form.AppForm>
  )
}

function CustomerBillingSettingsAsideForm({ customer, closeAside }) {
  const updateCustomerBillingSettingsMutation = useUpdateCustomerBillingSettingsMutation()
  const [houseAccountRevocationPayload, setHouseAccountRevocationPayload] = useState(null)
  const payLaterDefault = customer.pay_later_allowed !== false

  const saveBillingSettings = async (payload) => {
    await updateCustomerBillingSettingsMutation.mutateAsync(payload)
    setHouseAccountRevocationPayload(null)
    toast.success('Billing setup updated')
    closeAside?.()
  }

  const confirmHouseAccountRevocation = async () => {
    if (!houseAccountRevocationPayload) return

    try {
      await saveBillingSettings({
        ...houseAccountRevocationPayload,
        confirm_house_account_revocation: true,
      })
    } catch (error) {
      toast.error('Billing setup update failed', {
        description: getErrorMessage(error),
      })
      throw error
    }
  }

  const form = useAppForm({
    defaultValues: {
      pay_later_allowed: payLaterDefault,
      allow_fulfillment_before_payment:
        payLaterDefault && Boolean(customer.allow_fulfillment_before_payment),
      billing_email: customer.billing_email || '',
      statement_delivery_method: customer.statement_delivery_method || 'automatic',
      credit_limit: customer.credit_limit ?? '',
      payment_terms_code: customer.payment_terms_code || CustomerPaymentTerms.DueOnReceipt.Value,
      payment_terms_days:
        customer.payment_terms_code === CustomerPaymentTerms.Custom.Value
          ? String(customer.payment_terms_days ?? '')
          : '',
      tax_exempt: Boolean(customer.tax_exempt),
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        const paymentTermsCode = value.payment_terms_code || CustomerPaymentTerms.DueOnReceipt.Value
        const payLaterAllowed = value.pay_later_allowed !== false
        const allowFulfillmentBeforePayment =
          payLaterAllowed && value.allow_fulfillment_before_payment === true
        const customDays = String(value.payment_terms_days ?? '').trim()

        if (
          allowFulfillmentBeforePayment &&
          paymentTermsCode === CustomerPaymentTerms.Custom.Value &&
          customDays === ''
        ) {
          toast.error('Custom payment terms need a number of days.')
          return
        }

        const payload = buildCustomerBillingPayload(customer, value)

        try {
          await saveBillingSettings(payload)
        } catch (error) {
          if (
            error?.response?.status === 409 &&
            error?.response?.data?.code === 'house_account_revocation_confirmation_required'
          ) {
            setHouseAccountRevocationPayload(payload)
            return
          }

          toast.error('Billing setup update failed', {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })
  const paymentTermsCode = useStore(form.store, (state) => state.values.payment_terms_code)
  const payLaterAllowed = useStore(form.store, (state) => state.values.pay_later_allowed !== false)
  const allowFulfillmentBeforePayment = useStore(
    form.store,
    (state) => state.values.allow_fulfillment_before_payment === true
  )
  const houseAccountEnabled = isHouseAccountEnabled(payLaterAllowed, allowFulfillmentBeforePayment)

  return (
    <form.AppForm>
      <form.Form className="flex min-h-full flex-col">
        <OrderDetailPanel
          title="Billing Setup"
          subtitle="Update this customer's House Account rules, tax default, and statement email."
          icon={ReceiptText}
          tone="neutral"
          className="shadow-none"
        >
          <div className={ORDER_ASIDE_FORM_SURFACE_CLASS}>
            <form.FormError />
            <div className="grid gap-y-5">
              <HouseAccountSettingsSection
                enabled={houseAccountEnabled}
                onEnabledChange={(enabled) => setHouseAccountEnabled(form, enabled)}
              >
                <form.AppField name="pay_later_allowed">
                  {(field) => (
                    <BillingBooleanSettingField
                      field={field}
                      icon={HandCoins}
                      title="Allow Pay Later Orders"
                      description="Let this customer place orders before the full balance is collected."
                    />
                  )}
                </form.AppField>

                {!payLaterAllowed ? (
                  <Typography variant="supporting" wrap="pretty" className="block">
                    Turn Pay Later on before setting fulfillment-before-payment, terms, or a credit
                    limit.
                  </Typography>
                ) : null}

                <form.AppField name="allow_fulfillment_before_payment">
                  {(field) => (
                    <BillingBooleanSettingField
                      field={field}
                      icon={Truck}
                      title="Allow Fulfillment Before Payment"
                      description="Let orders be picked up or delivered before payment is collected."
                      disabled={!payLaterAllowed}
                      displayValue={payLaterAllowed && allowFulfillmentBeforePayment}
                    />
                  )}
                </form.AppField>

                <form.AppField name="credit_limit">
                  {(field) => (
                    <field.MoneyInput
                      label="Credit Limit"
                      placeholder="No limit"
                      showFreeBadge={false}
                      formatEmptyAsZero={false}
                      disabled={!payLaterAllowed}
                    />
                  )}
                </form.AppField>

                <form.AppField name="payment_terms_code">
                  {(field) => (
                    <field.SelectInput
                      label="Payment Terms"
                      options={getPaymentTermsOptions()}
                      disabled={!payLaterAllowed || !allowFulfillmentBeforePayment}
                      disabledTooltip={getPaymentTermsDisabledTooltip(
                        payLaterAllowed,
                        allowFulfillmentBeforePayment
                      )}
                    />
                  )}
                </form.AppField>
                {paymentTermsCode === CustomerPaymentTerms.Custom.Value ? (
                  <form.AppField name="payment_terms_days">
                    {(field) => (
                      <field.TextInput
                        label="Custom Days"
                        placeholder="30"
                        inputMode="numeric"
                        disabled={!payLaterAllowed || !allowFulfillmentBeforePayment}
                      />
                    )}
                  </form.AppField>
                ) : null}
              </HouseAccountSettingsSection>

              <form.AppField name="tax_exempt">
                {(field) => (
                  <BillingBooleanSettingField
                    field={field}
                    icon={BadgePercent}
                    title="Tax Exempt"
                    description="Mark this customer as exempt from sales tax."
                  />
                )}
              </form.AppField>

              <form.AppField name="statement_delivery_method">
                {(field) => (
                  <field.SelectInput
                    label="Statement delivery"
                    options={[
                      { name: 'Automatic (email when available)', id: 'automatic' },
                      { name: 'Email', id: 'email' },
                      { name: 'Print', id: 'print' },
                    ]}
                  />
                )}
              </form.AppField>
              <form.AppField name="billing_email">
                {(field) => (
                  <field.EmailInput
                    label="Billing Email"
                    placeholder={customer.email || 'billing@example.com'}
                  />
                )}
              </form.AppField>
            </div>
          </div>
        </OrderDetailPanel>
        <CustomerAsideFormFooter
          form={form}
          mutation={updateCustomerBillingSettingsMutation}
          saveLabel="Save Billing Setup"
          onCancel={closeAside}
        />
      </form.Form>
      <ConfirmationDialog
        trigger={null}
        open={Boolean(houseAccountRevocationPayload)}
        onOpenChange={(open) => {
          if (!open) setHouseAccountRevocationPayload(null)
        }}
        title="Turn Off House Account?"
        description="Orders with a balance will immediately require payment before they can move forward through fulfillment, including orders already in progress."
        confirmText="Turn Off Anyway"
        pendingText="Updating..."
        isPending={updateCustomerBillingSettingsMutation.isPending}
        closeOnError={false}
        onConfirm={confirmHouseAccountRevocation}
      />
    </form.AppForm>
  )
}

function CustomerContactsAsideForm({ customer, closeAside }) {
  const updateCustomerMutation = useUpdateCustomerMutation()
  const { isValidPhone } = usePhoneFormatter()
  const validateContactPhone = ({ value }) => {
    const phone = String(value || '').trim()

    if (phone && !isValidPhone(phone)) {
      return 'Enter a valid phone number.'
    }
  }
  const form = useAppForm({
    defaultValues: {
      contacts: contactDraftFromCustomer(customer),
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        const contacts = value.contacts ?? []
        const invalidPhoneIndex = contacts.findIndex((contact) => {
          const phone = String(contact.phone || '').trim()

          return phone && !isValidPhone(phone)
        })

        if (invalidPhoneIndex >= 0) {
          return {
            fields: {
              [`contacts.${invalidPhoneIndex}.phone`]: ['Enter a valid phone number.'],
            },
          }
        }

        try {
          await updateCustomerMutation.mutateAsync({
            customerId: customer.id,
            contacts: normalizeContactsPayload(contacts),
          })
          toast.success('Contacts updated')
          closeAside?.()
        } catch (error) {
          toast.error('Contacts update failed', {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })
  const contacts = useStore(form.store, (state) => state.values.contacts ?? [])

  const addContact = () =>
    form.pushFieldValue('contacts', {
      id: undefined,
      name: '',
      email: '',
      phone: '',
      is_primary: contacts.length === 0,
    })

  const removeContact = (index) => {
    const nextContacts = contacts.filter((_, contactIndex) => contactIndex !== index)

    if (nextContacts.length > 0 && !nextContacts.some((contact) => contact.is_primary)) {
      nextContacts[0] = { ...nextContacts[0], is_primary: true }
    }

    form.setFieldValue('contacts', nextContacts)
  }

  const makePrimary = (index) => {
    form.setFieldValue(
      'contacts',
      contacts.map((contact, contactIndex) => ({
        ...contact,
        is_primary: contactIndex === index,
      }))
    )
  }

  return (
    <form.AppForm>
      <OrderDetailPanel
        title="Contacts"
        subtitle="Add people who can place or receive billing communication for this customer."
        icon={ContactRound}
        tone="mint"
        className="flex min-h-0 flex-1 shadow-none [&>[data-slot=card-content]]:flex [&>[data-slot=card-content]]:min-h-0 [&>[data-slot=card-content]]:flex-1 [&>[data-slot=card-content]]:flex-col [&>[data-slot=card-content]]:space-y-0"
      >
        <form.Form
          className={cn(ORDER_ASIDE_FORM_SURFACE_CLASS, 'mt-4 flex min-h-0 flex-1 flex-col')}
        >
          <form.FormError />
          <ScrollArea className="-mr-4 min-h-0 flex-1">
            <div className="space-y-4 pr-4 pb-4">
              {contacts.length === 0 ? (
                <EmptyState size="compact" title="No contacts added yet." />
              ) : (
                contacts.map((contact, index) => (
                  <div
                    key={contact.id ?? `new-${index}`}
                    className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Typography variant="eyebrow">Contact {index + 1}</Typography>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant={contact.is_primary ? 'selection' : 'outline'}
                          size="sm"
                          onClick={() => makePrimary(index)}
                        >
                          Primary
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="Remove contact"
                          onClick={() => removeContact(index)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <form.AppField
                        name={`contacts.${index}.name`}
                        validators={{
                          onChange: customerContactSchema.shape.name,
                          onSubmit: customerContactSchema.shape.name,
                        }}
                      >
                        {(field) => (
                          <field.TextInput
                            id={`contact-${index}-name`}
                            label="Name"
                            placeholder="Contact name"
                          />
                        )}
                      </form.AppField>
                      <form.AppField
                        name={`contacts.${index}.email`}
                        validators={{
                          onChange: customerContactSchema.shape.email,
                          onSubmit: customerContactSchema.shape.email,
                        }}
                      >
                        {(field) => (
                          <field.EmailInput
                            id={`contact-${index}-email`}
                            label="Email"
                            placeholder="contact@example.com"
                          />
                        )}
                      </form.AppField>
                      <form.AppField
                        name={`contacts.${index}.phone`}
                        validators={{
                          onChange: validateContactPhone,
                          onSubmit: validateContactPhone,
                        }}
                      >
                        {(field) => (
                          <field.PhoneInput id={`contact-${index}-phone`} label="Phone" />
                        )}
                      </form.AppField>
                    </div>
                  </div>
                ))
              )}
              <Button type="button" variant="outline" onClick={addContact}>
                <Plus className="size-4" />
                Add Contact
              </Button>
            </div>
          </ScrollArea>
          <div className="-mx-5 shrink-0 border-t border-border/60 bg-background/95 px-5 pt-4 pb-1 backdrop-blur sm:-mx-6 sm:px-6">
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeAside}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateCustomerMutation.isPending}>
                {updateCustomerMutation.isPending ? 'Saving...' : 'Save Contacts'}
              </Button>
            </div>
          </div>
        </form.Form>
      </OrderDetailPanel>
    </form.AppForm>
  )
}

export function CustomerDetailPage() {
  return <CustomerDetailHub />
}

function CustomerDetailHub() {
  const { customerId, orgId } = Route.useParams({ strict: false })
  const { isReadOnly } = Route.useLoaderData()
  const ability = useAbility()
  const canViewReceivables = canViewCustomerReceivables(ability)
  const { data: customer } = useCustomer(customerId, { organizationId: orgId })
  const {
    data: receivables,
    isLoading: isReceivablesLoading,
    refetch: refetchReceivables,
  } = useCustomerReceivables(customerId, {
    organizationId: orgId,
    enabled: canViewReceivables && Boolean(customerId),
  })
  const canListSubscriptions = ability.can('list', 'flower-subscriptions')
  const { data: subscriptionResponse, isPending: subscriptionsPending } = useFlowerSubscriptions({
    organizationId: orgId,
    filters: {
      customerId,
      sort: 'attention',
    },
    page: 1,
    pageSize: 10,
    enabled: canListSubscriptions,
  })
  const path = usePathHelper()
  const navigate = useNavigate()
  const createPosCart = useCreateCustomerPosCartMutation()
  const [activeTab, setActiveTab] = useState('orders')
  const [isAsideOpen, setIsAsideOpen] = useState(false)
  const [asideView, setAsideView] = useState('')
  const [isStatementDialogOpen, setIsStatementDialogOpen] = useState(false)
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false)

  const metrics = customer.metrics || {}
  const orderHistory = customer.order_history || []
  const messages = customer.messages || []
  const occasionReminders = customer.occasion_reminders || []
  const internalNotes = Array.isArray(customer.internal_notes) ? customer.internal_notes : []
  const customerReviews = Array.isArray(customer.customer_reviews) ? customer.customer_reviews : []
  const totalMessages = Number(customer.messages_count ?? messages.length)
  const totalCustomerReviews = Number(customer.customer_reviews_count ?? customerReviews.length)
  const subscriptions = Array.isArray(subscriptionResponse)
    ? subscriptionResponse
    : (subscriptionResponse?.data ?? [])
  const totalSubscriptions = Number(subscriptionResponse?.meta?.total ?? subscriptions.length)
  const canSendCustomerStatements = !isReadOnly && canManageCustomerBilling(ability)

  const startCustomerOrder = async () => {
    try {
      const response = await createPosCart.mutateAsync(customer.id)
      await navigate({
        ...path.salesOrders.create().search({ cartId: response?.cart_id ?? response?.id }).link,
      })
    } catch (error) {
      toast.error('Failed to create POS cart', {
        description: getErrorMessage(error),
      })
    }
  }

  const openCustomerAside = (view) => {
    setAsideView(view)
    setIsAsideOpen(true)
  }

  const refreshReceivables = () => {
    if (!canViewReceivables || !customerId) {
      return
    }

    void refetchReceivables()
  }

  const openStatementDialog = () => {
    refreshReceivables()
    setIsStatementDialogOpen(true)
  }

  const openPaymentDialog = () => {
    refreshReceivables()
    setIsPaymentDialogOpen(true)
  }

  return (
    <div className="-mr-4 mb-10">
      <div className="pr-6 pt-1">
        <AdminDetailGrid
          main={
            <>
              <CustomerDetailHeader
                customer={customer}
                receivables={receivables}
                metrics={metrics}
                createPosCart={createPosCart}
                onCreateOrder={startCustomerOrder}
                onOpenPayment={openPaymentDialog}
                onOpenStatement={openStatementDialog}
                isReadOnly={isReadOnly}
              />

              <CustomerSummaryStrip
                customer={customer}
                receivables={receivables}
                metrics={metrics}
              />

              <CustomerUnpaidOrdersCard
                receivables={receivables}
                onOpenStatement={openStatementDialog}
              />

              <CustomerActivityWorkspace
                value={activeTab}
                onValueChange={setActiveTab}
                customer={customer}
                orderHistory={orderHistory}
                subscriptions={subscriptions}
                totalSubscriptions={totalSubscriptions}
                subscriptionsPending={subscriptionsPending}
                occasionReminders={occasionReminders}
                messages={messages}
                totalMessages={totalMessages}
                customerReviews={customerReviews}
                totalCustomerReviews={totalCustomerReviews}
                internalNotes={internalNotes}
              />
            </>
          }
          aside={
            <>
              <CustomerContactAside
                customer={customer}
                onEditCustomer={() => openCustomerAside(customerAsideViews.editCustomer)}
                onEditPhysicalAddress={() =>
                  openCustomerAside(customerAsideViews.editPhysicalAddress)
                }
                onEditBillingAddress={() =>
                  openCustomerAside(customerAsideViews.editBillingAddress)
                }
                onEditContacts={() => openCustomerAside(customerAsideViews.editContacts)}
              />
              <CustomerBillingSetupCard
                customer={customer}
                onEditBillingSettings={() =>
                  openCustomerAside(customerAsideViews.editBillingSettings)
                }
              />
              <CustomerCardsOnFileCard customer={customer} />
            </>
          }
        />
      </div>
      <CustomerAside open={isAsideOpen} onOpenChange={setIsAsideOpen}>
        {asideView === customerAsideViews.editCustomer ? (
          <CustomerIdentityAsideForm customer={customer} closeAside={() => setIsAsideOpen(false)} />
        ) : null}
        {asideView === customerAsideViews.editPhysicalAddress ? (
          <CustomerAddressAsideForm
            customer={customer}
            addressType="physical_address"
            closeAside={() => setIsAsideOpen(false)}
          />
        ) : null}
        {asideView === customerAsideViews.editBillingAddress ? (
          <CustomerAddressAsideForm
            customer={customer}
            addressType="billing_address"
            closeAside={() => setIsAsideOpen(false)}
          />
        ) : null}
        {asideView === customerAsideViews.editBillingSettings ? (
          <Can do="manage" on="customers" field="billing">
            <ReadOnly isReadOnly={false} overrideRoute>
              <CustomerBillingSettingsAsideForm
                customer={customer}
                closeAside={() => setIsAsideOpen(false)}
              />
            </ReadOnly>
          </Can>
        ) : null}
        {asideView === customerAsideViews.editContacts ? (
          <CustomerContactsAsideForm customer={customer} closeAside={() => setIsAsideOpen(false)} />
        ) : null}
      </CustomerAside>
      <CustomerStatementDialog
        customer={customer}
        organizationId={orgId}
        receivables={receivables}
        isReceivablesLoading={isReceivablesLoading}
        canSendEmail={canSendCustomerStatements}
        open={isStatementDialogOpen}
        onOpenChange={setIsStatementDialogOpen}
      />
      <CustomerPaymentDialog
        customer={customer}
        receivables={receivables}
        open={isPaymentDialogOpen}
        onOpenChange={setIsPaymentDialogOpen}
      />
    </div>
  )
}
